import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { v: "yes", label: "Yes — happy to chat", help: "We'll call you to introduce ourselves." },
  { v: "maybe", label: "Maybe — send more info", help: "We'll email our landowner pack first." },
  { v: "not_now", label: "Not right now", help: "We'll check back in 12 months — nothing more." },
  { v: "remove", label: "Please remove me", help: "We'll suppress your address from all future mailings." },
];

export default function LetterResponse() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    response_code: "",
    campaign_code: "",
    name: "", email: "", phone: "",
    location: "", acreage: "",
    interest: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setForm(f => ({
      ...f,
      response_code: params.get("code") || params.get("ref") || "",
      campaign_code: params.get("c") || "",
    }));
  }, [params]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pickInterest = (v) => setForm({ ...form, interest: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter your name."); return; }
    if (!form.response_code.trim()) { toast.error("Please enter the reference code from your letter."); return; }
    if (!form.email.trim() && !form.phone.trim()) { toast.error("Please give us an email or a phone number."); return; }
    if (!form.interest) { toast.error("Please choose an option below."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/letter-response`, form);
      setDone(true);
      toast.success("Thank you — your response has been received.");
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally { setLoading(false); }
  };

  if (done) {
    const removed = form.interest === "remove";
    return (
      <div data-testid="letter-success" className="bg-[#0B0F0E] min-h-[80vh]">
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <CheckCircle2 className="text-[#C9A24B] mx-auto" size={56} />
          <h1 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-7 leading-[0.95]">
            {removed ? "You've been removed." : "Thank you."}
          </h1>
          <p className="text-[#9BA39B] text-lg mt-6 max-w-xl mx-auto leading-relaxed">
            {removed
              ? "Your address has been suppressed. You will not receive further mailings from Upsurge Strategic Land."
              : "Your response has been received with reference "}
            {!removed && <span className="text-[#C9A24B] font-semibold">{form.response_code}</span>}
            {!removed && ". A member of our team will be in touch personally."}
          </p>
          <Link to="/" data-testid="letter-home-link" className="usl-btn-outline mt-10 inline-flex">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="letter-response-page" className="bg-[#0B0F0E]">
      <Toaster position="top-center" richColors theme="dark" />
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
        <div className="overline"><span className="h-line" />Letter Response Portal</div>
        <h1 className="font-display text-[#F4EFE6] text-5xl md:text-7xl mt-7 leading-[0.95] max-w-3xl">
          You received a letter from us — <span className="italic-gold">thank you for reading.</span>
        </h1>
        <p className="text-[#9BA39B] text-lg max-w-2xl mt-8 leading-relaxed">
          Use the form below to tell us how you&apos;d like to be contacted — or to opt out of future mailings. Your information stays entirely confidential.
        </p>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pb-28">
        <form data-testid="letter-form" onSubmit={submit} className="bg-[#11171A] border border-[#F4EFE6]/10 p-8 md:p-12 space-y-9">
          {/* Code block */}
          <div className="grid md:grid-cols-2 gap-7">
            <div>
              <label className="overline block mb-2">Reference code (from your letter) *</label>
              <input data-testid="letter-code" className="underline-input tracking-[0.2em] uppercase" value={form.response_code} onChange={onChange("response_code")} placeholder="USL-XXXXX" />
            </div>
            <div>
              <label className="overline block mb-2">Campaign (optional)</label>
              <input data-testid="letter-campaign" className="underline-input tracking-[0.2em] uppercase" value={form.campaign_code} onChange={onChange("campaign_code")} placeholder="CAMP-XXXXX" />
            </div>
          </div>

          {/* Interest selection */}
          <div>
            <label className="overline block mb-4">Your response *</label>
            <div className="grid sm:grid-cols-2 gap-3">
              {INTERESTS.map(i => (
                <button
                  type="button"
                  key={i.v}
                  data-testid={`letter-interest-${i.v}`}
                  onClick={() => pickInterest(i.v)}
                  className={`text-left p-5 border transition-all duration-200 ${
                    form.interest === i.v
                      ? "border-[#C9A24B] bg-[#C9A24B]/10"
                      : "border-[#F4EFE6]/15 hover:border-[#C9A24B]/50"
                  }`}
                >
                  <div className="font-display text-xl text-[#F4EFE6]">{i.label}</div>
                  <div className="text-xs text-[#9BA39B] mt-2">{i.help}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="grid md:grid-cols-2 gap-7">
            <div>
              <label className="overline block mb-2">Your name *</label>
              <input data-testid="letter-name" className="underline-input" value={form.name} onChange={onChange("name")} />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input data-testid="letter-email" type="email" className="underline-input" value={form.email} onChange={onChange("email")} />
            </div>
            <div>
              <label className="overline block mb-2">Telephone</label>
              <input data-testid="letter-phone" className="underline-input" value={form.phone} onChange={onChange("phone")} />
            </div>
            <div>
              <label className="overline block mb-2">Site location</label>
              <input data-testid="letter-location" className="underline-input" value={form.location} onChange={onChange("location")} placeholder="Village / town" />
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Anything to add?</label>
            <textarea data-testid="letter-notes" rows={4} className="underline-input resize-none" value={form.notes} onChange={onChange("notes")} />
          </div>

          <div className="pt-1">
            <button data-testid="letter-submit" type="submit" disabled={loading} className="usl-btn">
              {loading ? "Sending..." : "Send Response"} <ArrowUpRight size={16} />
            </button>
            <p className="text-xs text-[#9BA39B] mt-4 max-w-xl">We use your details only to respond to this letter. We never sell or share your information.</p>
          </div>
        </form>
      </section>
    </div>
  );
}
